import { graphql } from 'react-relay';
import { useCallback, useContext } from 'react';
import { useMutationInterop } from '@confluence/apollo-relay-interop';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { containsNetworkError } from '@confluence/network';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { retry } from '@confluence/retry';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { MARK_COMMENTS_AS_READ_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';

import type {
	useMarkCommentsAsReadMutation as useMarkCommentsAsReadMutation$data,
	useMarkCommentsAsReadMutationVariables as useMarkCommentsAsReadMutation$variables,
} from './__types__/useMarkCommentsAsReadMutation.ts';
const markCommentsAsReadMutation = graphql`
	mutation useMarkCommentsAsReadMutation($commentIds: [ID!]!) {
		markCommentsAsRead(input: { commentIds: $commentIds }) {
			success
			errors
		}
	}
`;

export const useMarkCommentsAsRead = (): ((commentIds: string[]) => Promise<void>) => {
	const [markCommentsAsRead] = useMutationInterop<
		useMarkCommentsAsReadMutation$data,
		useMarkCommentsAsReadMutation$variables
	>(markCommentsAsReadMutation);
	const experienceTracker = useContext(ExperienceTrackerContext);

	const markCommentsAsReadFn = useCallback(
		async (commentIds: string[]) => {
			if (commentIds.length === 0) {
				return;
			}

			// Start experience tracking for marking comments as read
			experienceTracker.start({
				name: MARK_COMMENTS_AS_READ_EXPERIENCE,
				attributes: {
					commentIds,
					commentCount: commentIds.length,
				},
			});

			let attemptNumber = 1;
			try {
				const result = await retry(
					async (attempt) => {
						attemptNumber = attempt + 1;
						return await markCommentsAsRead({
							variables: { commentIds },
						});
					},
					{},
					async (error) => {
						// Don't retry for expected errors (403/unauthorized, network errors)
						return !isUnauthorizedError(error) && !containsNetworkError(error);
					},
				);

				// Check if the mutation succeeded
				if (result?.data?.markCommentsAsRead?.success) {
					// Mark experience as successful
					experienceTracker.succeed({
						name: MARK_COMMENTS_AS_READ_EXPERIENCE,
						attributes: {
							commentIds,
							commentCount: commentIds.length,
							attemptNumber,
						},
					});
				} else {
					// Backend returned success: false
					experienceTracker.fail({
						name: MARK_COMMENTS_AS_READ_EXPERIENCE,
						error: new Error('Backend returned success: false'),
						attributes: {
							attemptNumber,
						},
					});
				}
			} catch (err) {
				// Handle expected errors (403/unauthorized, network errors) - abort experience tracking
				if (isUnauthorizedError(err) || containsNetworkError(err)) {
					experienceTracker.abort({
						name: MARK_COMMENTS_AS_READ_EXPERIENCE,
						reason: 'Expected error (unauthorized or network error)',
					});
					markErrorAsHandled(err);
					return;
				}

				// Mark experience as failed for unexpected errors
				experienceTracker.fail({
					name: MARK_COMMENTS_AS_READ_EXPERIENCE,
					error: err,
					attributes: {
						attemptNumber,
					},
				});

				// Handle unexpected errors (including 500) - track them with context
				setContentErrorAttributes(err, {
					objectType: 'comment',
				});

				getMonitoringClient().submitError(err, {
					attribution: Attribution.COMMENTS,
				});

				markErrorAsHandled(err);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- experienceTracker from context doesn't need to be in deps
		[markCommentsAsRead],
	);

	return markCommentsAsReadFn;
};
